import { useMutation } from '@tanstack/react-query';
import { setCitySubscription } from '../../api/storefront';
import { generateCitySitemap } from '../../api/cities';

interface UpdateCitySubscriptionParams {
  organizationId: string;
  selectedCities: string[];
  currentCities: string[];
}

/**
 * Update a tenant's city subscriptions via the Storefront service
 * (Spanner OrganizationCities). No Firestore.
 */
export const useUpdateCitySubscription = () => {
  return useMutation({
    mutationFn: async ({
      organizationId,
      selectedCities,
      currentCities,
    }: UpdateCitySubscriptionParams) => {
      const toAdd = selectedCities.filter((c) => !currentCities.includes(c));
      const toRemove = currentCities.filter((c) => !selectedCities.includes(c));

      if (!toAdd.length && !toRemove.length) {
        return { success: true, message: 'No changes detected' };
      }

      await Promise.all([
        ...toAdd.map((cityId) => setCitySubscription(organizationId, cityId, 'subscribe')),
        ...toRemove.map((cityId) => setCitySubscription(organizationId, cityId, 'unsubscribe')),
      ]);

      // Sitemap regen is best-effort — don't fail the subscription if it errors.
      try {
        await generateCitySitemap(organizationId);
      } catch (e) {
        console.warn('City sitemap regeneration failed (subscription still saved):', e);
      }

      return {
        success: true,
        message: 'City subscriptions updated successfully',
        added: toAdd,
        removed: toRemove,
      };
    },
  });
};
